/**
 * Onboarding Wizard — 4-step setup dla nowego tenanta.
 *
 * Krok 1: Welcome + persona detection (bidder / producer / mixed)
 * Krok 2: Industry selection (z 12 templates: oswietlenie/hvac/meble/elektryka/...)
 * Krok 3: Tenant info (nazwa firmy, NIP opcjonalnie)
 * Krok 4: First action (scan tenders / upload katalogu / explore demo data)
 *
 * Po zakończeniu wizardu redirect do /tenders lub /producer/dashboard zależnie od persony.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Zap, Building2, Factory, Sparkles, CheckCircle2, ArrowRight, ArrowLeft,
  Lightbulb, Wind, Sofa, Plug, Flame, FlaskConical, Wheat, Coins, Bolt, Home, Package, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

const PERSONAS = [
  {
    key: 'bidder',
    icon: Building2,
    title_pl: 'Handlowiec — startuję w przetargach',
    title_en: 'Sales rep — bidding in tenders',
    desc_pl: 'Szukam przetargów publicznych, analizuję, składam oferty, wygrywam kontrakty.',
    desc_en: 'Find public tenders, analyze, submit offers, win contracts.',
    plan_recommend: 'Pro 2,499 PLN/m + 1% success fee',
  },
  {
    key: 'investor',
    icon: Coins,
    title_pl: 'Inwestor — szukam okazji w wielu branżach',
    title_en: 'Investor — multi-industry deal hunter',
    desc_pl: 'Skanuję przetargi we wszystkich branżach z najlepszym ROI. Wybieram tylko te warte czasu.',
    desc_en: 'Scan tenders across all industries for best ROI. Pick only worthwhile ones.',
    plan_recommend: 'Business 4,990 PLN/m + 2% success fee',
  },
  {
    key: 'producer',
    icon: Factory,
    title_pl: 'Producent — chcę żeby moje produkty były oferowane',
    title_en: 'Producer — want my products in offers',
    desc_pl: 'Mój katalog produktów ma być widoczny dla setek handlowców startujących w przetargach.',
    desc_en: 'My product catalog visible to hundreds of bidders.',
    plan_recommend: 'Producer Pro 899 PLN/m + 3-5% finder fee',
  },
];

const INDUSTRY_ICONS = {
  oswietlenie: Lightbulb,
  hvac: Wind,
  meble: Sofa,
  elektryka: Plug,
  gaz: Flame,
  chemia: FlaskConical,
  rolnictwo: Wheat,
  fintech: Coins,
  energetyka: Bolt,
  nieruchomosci: Home,
  inne: Package,
  przetargi_ogolne: Briefcase,
};

export default function Onboarding({ lang = 'pl', tenantId }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [persona, setPersona] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [tenantInfo, setTenantInfo] = useState({ name: '', nip: '', city: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/settings/industries').then(r => setIndustries(r.data || [])).catch(() => setIndustries([]));
  }, []);

  async function finish(action) {
    setSaving(true);
    try {
      // Save industry
      if (selectedIndustry) {
        await api.post('/settings/industry', { industry: selectedIndustry });
      }
      // Save persona to tenant metadata
      await api.post('/settings/tenant-metadata', {
        persona,
        company_name: tenantInfo.name,
        company_nip: tenantInfo.nip,
        company_city: tenantInfo.city,
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
      }).catch(() => null); // graceful — endpoint może nie istnieć

      toast.success(t(lang, 'Konto skonfigurowane!', 'Account configured!'));

      // Route based on persona + action
      if (persona === 'producer') {
        navigate('/suppliers');
      } else if (action === 'scan') {
        navigate('/tenders');
      } else if (action === 'demo') {
        navigate('/tenders/TEST-LED-001');
      } else {
        navigate('/home-v3');
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  const stepProgress = (step / 4) * 100;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-background to-muted/20">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              <CardTitle className="text-lg">MASKIL Setup</CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">{t(lang, `Krok ${step}/4`, `Step ${step}/4`)}</Badge>
          </div>
          <Progress value={stepProgress} className="h-1" />
        </CardHeader>

        <CardContent className="space-y-4">
          {step === 1 && <StepPersona persona={persona} setPersona={setPersona} lang={lang} />}
          {step === 2 && (
            <StepIndustry
              industries={industries}
              selectedIndustry={selectedIndustry}
              setSelectedIndustry={setSelectedIndustry}
              persona={persona}
              lang={lang}
            />
          )}
          {step === 3 && (
            <StepTenantInfo
              info={tenantInfo}
              setInfo={setTenantInfo}
              lang={lang}
            />
          )}
          {step === 4 && (
            <StepFirstAction
              persona={persona}
              industry={selectedIndustry}
              onFinish={finish}
              saving={saving}
              lang={lang}
            />
          )}

          {/* Navigation */}
          {step < 4 && (
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setStep(s => Math.max(1, s - 1))}
                disabled={step === 1}
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                {t(lang, 'Wstecz', 'Back')}
              </Button>
              <Button
                onClick={() => setStep(s => Math.min(4, s + 1))}
                disabled={
                  (step === 1 && !persona) ||
                  (step === 2 && !selectedIndustry && persona !== 'investor') ||
                  (step === 3 && !tenantInfo.name)
                }
              >
                {t(lang, 'Dalej', 'Next')}
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StepPersona({ persona, setPersona, lang }) {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-medium">{t(lang, 'Kim jesteś?', 'Who are you?')}</h3>
      <p className="text-sm text-muted-foreground">
        {t(lang,
          'Dopasujemy MASKIL do Twojego workflow. Możesz zmienić później.',
          'We tailor MASKIL to your workflow. Change later if needed.')}
      </p>
      <div className="space-y-2">
        {PERSONAS.map(p => {
          const Icon = p.icon;
          const selected = persona === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setPersona(p.key)}
              data-testid={`onboarding-persona-${p.key}`}
              className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${selected ? 'text-primary' : ''}`} />
                <div className="flex-1">
                  <div className="font-medium text-sm">{lang === 'pl' ? p.title_pl : p.title_en}</div>
                  <div className="text-xs text-muted-foreground mt-1">{lang === 'pl' ? p.desc_pl : p.desc_en}</div>
                  <div className="text-xs text-primary mt-1.5">💡 {p.plan_recommend}</div>
                </div>
                {selected && <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepIndustry({ industries, selectedIndustry, setSelectedIndustry, persona, lang }) {
  if (persona === 'investor') {
    return (
      <div className="space-y-3">
        <h3 className="text-base font-medium">{t(lang, 'Branże inwestycyjne', 'Investment industries')}</h3>
        <p className="text-sm text-muted-foreground">
          {t(lang,
            'Inwestor multi-industry skanuje WSZYSTKIE branże. ROI Radar pokaże najlepsze deal\'e niezależnie od kategorii.',
            'Multi-industry investor scans ALL categories. ROI Radar shows best deals across all.')}
        </p>
        <div className="p-3 bg-primary/5 border border-primary/20 rounded">
          <p className="text-sm">
            ✓ {t(lang, 'Aktywne wszystkie 12 branż automatycznie', 'All 12 industries active automatically')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t(lang, 'Możesz to zmienić w Settings → Industry.', 'Change later in Settings → Industry.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-base font-medium">{t(lang, 'Wybierz branżę', 'Choose your industry')}</h3>
      <p className="text-sm text-muted-foreground">
        {t(lang,
          'Industry templates sterują match score, ghost network, RFQ targeting. Możesz zmienić później.',
          'Industry templates drive match score, ghost network, RFQ targeting.')}
      </p>
      <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
        {industries.map(ind => {
          const Icon = INDUSTRY_ICONS[ind.id] || Package;
          const selected = selectedIndustry === ind.id;
          return (
            <button
              key={ind.id}
              type="button"
              onClick={() => setSelectedIndustry(ind.id)}
              data-testid={`onboarding-industry-${ind.id}`}
              className={`p-3 text-left rounded-lg border-2 transition-all ${
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${selected ? 'text-primary' : ''}`} />
                <span className="text-sm font-medium">{ind.name}</span>
                {selected && <CheckCircle2 className="w-3.5 h-3.5 text-primary ml-auto" />}
              </div>
              {ind.description && (
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{ind.description}</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepTenantInfo({ info, setInfo, lang }) {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-medium">{t(lang, 'Twoja firma', 'Your company')}</h3>
      <p className="text-sm text-muted-foreground">
        {t(lang, 'Wpisz podstawowe dane. NIP pozwoli auto-importować informacje z KRS.',
              'Enter company basics. NIP enables auto-import from KRS.')}
      </p>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">{t(lang, 'Nazwa firmy', 'Company name')} *</Label>
          <Input
            value={info.name}
            onChange={e => setInfo({ ...info, name: e.target.value })}
            placeholder={t(lang, 'np. Maskil LED Sp. z o.o.', 'e.g. Maskil LED Sp. z o.o.')}
            autoFocus
            data-testid="onboarding-company-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">NIP</Label>
            <Input
              value={info.nip}
              onChange={e => setInfo({ ...info, nip: e.target.value })}
              placeholder="np. 5360011120"
              data-testid="onboarding-nip"
            />
          </div>
          <div>
            <Label className="text-xs">{t(lang, 'Miasto', 'City')}</Label>
            <Input
              value={info.city}
              onChange={e => setInfo({ ...info, city: e.target.value })}
              placeholder="np. Warszawa"
              data-testid="onboarding-city"
            />
          </div>
        </div>
        {info.nip && info.nip.length === 10 && (
          <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-300">
            ✓ {t(lang, 'NIP wygląda poprawnie. Auto-import danych z KRS po zapisie.',
                     'NIP looks valid. KRS auto-import after save.')}
          </div>
        )}
      </div>
    </div>
  );
}

function StepFirstAction({ persona, industry, onFinish, saving, lang }) {
  const actions = persona === 'producer' ? [
    {
      key: 'upload',
      icon: Package,
      title: t(lang, 'Wgraj katalog produktów', 'Upload product catalog'),
      desc: t(lang, 'CSV/Excel z produktami. AI rozpozna kolumny.',
                  'CSV/Excel — AI detects columns.'),
    },
    {
      key: 'demo',
      icon: Sparkles,
      title: t(lang, 'Zobacz przykładowy producent', 'Explore sample producer'),
      desc: t(lang, 'Mock data z AQform — zobaczysz dashboard, placements, finder fees.',
                  'Mock AQform data — see dashboard, placements, fees.'),
    },
  ] : [
    {
      key: 'scan',
      icon: Building2,
      title: t(lang, 'Pokaż mi aktualne przetargi', 'Show me live tenders'),
      desc: t(lang, '1056 ogłoszeń w bazie — match score, deadlines, mapa.',
                  '1056 tenders in DB — match score, deadlines, map.'),
    },
    {
      key: 'demo',
      icon: Sparkles,
      title: t(lang, 'Demo: pełen flow od A do Z', 'Demo: full pipeline A to Z'),
      desc: t(lang, 'TEST-LED-001 — BoQ + RFQ + Profit Engine + Offer + JEDZ.',
                  'TEST-LED-001 — BoQ + RFQ + Profit Engine + Offer + JEDZ.'),
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-base font-medium">{t(lang, 'Zaczynamy!', 'Let\'s start!')}</h3>
      <p className="text-sm text-muted-foreground">
        {t(lang,
          'Konfiguracja gotowa. Co chcesz zrobić jako pierwsze?',
          'Setup ready. What\'s your first move?')}
      </p>
      <div className="space-y-2">
        {actions.map(a => {
          const Icon = a.icon;
          return (
            <button
              key={a.key}
              type="button"
              onClick={() => onFinish(a.key)}
              disabled={saving}
              data-testid={`onboarding-finish-${a.key}`}
              className="w-full p-3 text-left rounded-lg border-2 border-border hover:border-primary/50 hover:bg-muted/30 transition-all disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{a.desc}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
      </div>
      {saving && (
        <div className="text-xs text-center text-muted-foreground">
          {t(lang, 'Zapisuję konfigurację...', 'Saving...')}
        </div>
      )}
    </div>
  );
}
